import { exceptionApi } from '../api/exceptionApi';
import { ExceptionGroupItem, ExceptionLogItem, ExceptionLogDetail } from '../types/exception';
import { PaginationData } from '../models/paginationData';
import { useBaseQuery, useNoDataMutation, useInvalidateQueries } from './useBaseQuery';

export const exceptionKeys = {
    all: ['exception'] as const,
    groups: (page: number, size: number, isResolved?: boolean) =>
        [...exceptionKeys.all, 'groups', page, size, isResolved] as const,
    groupLogs: (groupId: number, page: number) =>
        [...exceptionKeys.all, 'groupLogs', groupId, page] as const,
    log: (logId: number) =>
        [...exceptionKeys.all, 'log', logId] as const,
};

export const useExceptionGroups = (page: number, size: number, isResolved?: boolean) =>
    useBaseQuery<PaginationData<ExceptionGroupItem>>(
        exceptionKeys.groups(page, size, isResolved),
        () => exceptionApi.getGroups(page, size, isResolved),
        { staleTime: 30 * 1000 },
    );

export const useExceptionGroupLogs = (groupId: number, page: number, size = 30) =>
    useBaseQuery<PaginationData<ExceptionLogItem>>(
        exceptionKeys.groupLogs(groupId, page),
        () => exceptionApi.getGroupLogs(groupId, page, size),
        { enabled: groupId > 0, staleTime: 30 * 1000 },
    );

export const useExceptionLog = (logId: number) =>
    useBaseQuery<ExceptionLogDetail>(
        exceptionKeys.log(logId),
        () => exceptionApi.getLog(logId),
        { enabled: logId > 0, staleTime: 60 * 1000 },
    );

export const useResolveExceptionGroup = () => {
    const { invalidate } = useInvalidateQueries();
    return useNoDataMutation(
        ({ groupId, isResolved }: { groupId: number; isResolved: boolean }) =>
            exceptionApi.resolveGroup(groupId, isResolved),
        {
            onSuccess: () => invalidate(exceptionKeys.all),
        },
    );
};
